import { useState } from "react";
import { Card, CardHeader, CardBody, DataTable, PageError, PageLoading } from "../components/index.js";
import { Field, FieldLabel, SectionLabel } from "../components/AdminForm.jsx";
import { useApi, useApiAction } from "../hooks/useApi.js";

/**
 * Organization admin (SUPER_ADMIN). Three cards: the agency's own identity,
 * its divisions/brands, and its capabilities.
 *
 * ZERO ORGANIZATIONS IS A VALID STATE: between the deploy and the seed run,
 * GET /api/organization returns { organization: null }. This page renders an
 * empty state for that — never an error — and the Divisions and Capabilities
 * cards keep LISTING whatever rows already exist. CREATING a division or a
 * capability is blocked until the seed runs, because both need an owner.
 */
export function AdminOrganization() {
  const orgApi = useApi("/api/organization");
  const divApi = useApi("/api/divisions");
  const capApi = useApi("/api/capabilities?includeInactive=true");
  const [notice, setNotice] = useState(null);

  const loading = orgApi.loading || divApi.loading || capApi.loading;
  const error = orgApi.error || divApi.error || capApi.error;
  if (error) return <PageError error={error} onRetry={() => { orgApi.reload(); divApi.reload(); capApi.reload(); }} />;
  if (loading) return <PageLoading />;

  const organization = orgApi.data?.organization ?? null;

  return (
    <div className="space-y-5 max-w-[1100px]">
      {notice && <div className="px-1 text-[12px] text-text-secondary">{notice}</div>}
      <IdentityCard organization={organization} onSaved={(m) => { setNotice(m); orgApi.reload({ silent: true }); }} />
      <DivisionsCard organization={organization} divisions={divApi.data?.divisions || []} onSaved={(m) => { setNotice(m); divApi.reload({ silent: true }); }} />
      <CapabilitiesCard organization={organization} capabilities={capApi.data?.capabilities || []} onSaved={(m) => { setNotice(m); capApi.reload({ silent: true }); }} />
    </div>
  );
}

function IdentityCard({ organization, onSaved }) {
  const api = useApiAction();
  const [form, setForm] = useState({
    name: organization?.name || "",
    legalName: organization?.legalName || "",
    websiteUrl: organization?.websiteUrl || "",
    logoUrl: organization?.logoUrl || "",
    accentColor: organization?.accentColor || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  if (!organization) {
    return (
      <Card>
        <CardHeader title="Organization" subtitle="Not configured yet" />
        <CardBody>
          <p className="text-[13px] text-text-secondary">
            No organization has been created yet. Run <code className="font-mono text-[12px]">seed-organization.js</code> via
            the Railway CLI to create it, then reload this page. Existing divisions and capabilities are still listed and
            editable below, but new ones can't be added until the organization exists to own them.
          </p>
        </CardBody>
      </Card>
    );
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await api("/api/organization", {
        method: "PUT",
        body: JSON.stringify({
          name: form.name,
          legalName: form.legalName || null,
          websiteUrl: form.websiteUrl || null,
          logoUrl: form.logoUrl || null,
          accentColor: form.accentColor || null,
        }),
      });
      onSaved("Organization updated.");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader title="Organization" subtitle="The agency's own identity" />
      <CardBody>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 max-w-[720px]">
          <Field label="Name" required value={form.name} onChange={(v) => set({ name: v })} />
          <Field label="Legal name" value={form.legalName} onChange={(v) => set({ legalName: v })} />
          <Field label="Website" placeholder="https://…" value={form.websiteUrl} onChange={(v) => set({ websiteUrl: v })} />
          <Field label="Logo URL" placeholder="https://…" value={form.logoUrl} onChange={(v) => set({ logoUrl: v })} />
          <Field label="Accent color" placeholder="#ff6804" value={form.accentColor} onChange={(v) => set({ accentColor: v })} />
        </div>
        <p className="mt-2 text-[11px] text-text-muted">
          Used by upcoming branded documents; not yet applied to PDFs or public pages.
        </p>
        {err && <div className="mt-3 text-[12px] text-negative">{err}</div>}
        <button type="button" disabled={busy} onClick={save} className="mt-4 h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50">
          {busy ? "Saving…" : "Save"}
        </button>
      </CardBody>
    </Card>
  );
}

/**
 * Divisions — the agency's brands. POST /api/divisions returns 409 without an
 * organization, so `+ Add Division` disables in that window (organization ===
 * null); editing, deactivating/reactivating and reordering existing rows stay
 * enabled the whole time since none of those need an owner assigned. Listed
 * with NO activeOnly filter (default-inclusive) so inactive brands can be
 * reactivated from here. There is no delete — only isActive.
 */
function DivisionsCard({ organization, divisions, onSaved }) {
  const api = useApiAction();
  const [form, setForm] = useState(null); // { mode: "add" } | { mode: "edit", division }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const sorted = [...divisions].sort((a, b) => a.sortOrder - b.sortOrder);
  const canAdd = !!organization;

  async function toggleActive(d) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/divisions/${d.id}`, { method: "PUT", body: JSON.stringify({ isActive: !d.isActive }) });
      onSaved(d.isActive ? `${d.name} deactivated.` : `${d.name} reactivated.`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function move(i, dir) {
    // F1 fix: assign positional indices to the WHOLE list rather than swapping
    // the two rows' sortOrder VALUES. Every pre-existing division shares
    // sortOrder = 0 (the column was added NOT NULL DEFAULT 0 and the seed only
    // ever set it for capabilities), so a value swap writes 0 into 0 — a
    // silent no-op that left the list alphabetical while the UI still claimed
    // "Reordered."
    const next = [...sorted];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    setBusy(true);
    setErr(null);
    try {
      for (const [idx, d] of next.entries()) {
        if (d.sortOrder !== idx) {
          await api(`/api/divisions/${d.id}`, { method: "PUT", body: JSON.stringify({ sortOrder: idx }) });
        }
      }
      onSaved("Reordered.");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title="Divisions"
        subtitle="Brands under the agency"
        right={
          <button
            type="button"
            disabled={!canAdd}
            title={canAdd ? undefined : "Create the organization first — run seed-organization.js"}
            onClick={() => setForm({ mode: "add" })}
            className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50"
          >
            + Add Division
          </button>
        }
      />
      <DataTable
        emptyLabel="No divisions yet."
        rows={sorted}
        columns={[
          {
            key: "name", label: "Division",
            render: (r) => (
              <span className="inline-flex items-center gap-2">
                <span className="w-3 h-3 rounded-full flex-shrink-0 border border-border-default" style={{ backgroundColor: r.accentColor || "transparent" }} />
                <span className="font-medium text-text-primary">{r.name}</span>
              </span>
            ),
          },
          { key: "slug", label: "Slug", render: (r) => <span className="font-mono text-[12px] text-text-muted">{r.slug}</span> },
          { key: "clientCount", label: "Clients", align: "right", render: (r) => r.clientCount ?? 0 },
          { key: "isActive", label: "Status", render: (r) => (r.isActive ? "Active" : "Inactive") },
          {
            key: "actions", label: "", align: "right",
            render: (r) => {
              const i = sorted.findIndex((x) => x.id === r.id);
              return (
                <span className="inline-flex items-center gap-2 justify-end whitespace-nowrap">
                  <button type="button" disabled={busy || i === 0} onClick={() => move(i, -1)} className="text-text-muted disabled:opacity-30" aria-label="Move up">↑</button>
                  <button type="button" disabled={busy || i === sorted.length - 1} onClick={() => move(i, 1)} className="text-text-muted disabled:opacity-30" aria-label="Move down">↓</button>
                  <button type="button" onClick={() => setForm({ mode: "edit", division: r })} className="text-[12px] text-accent hover:underline">Edit</button>
                  <button type="button" disabled={busy} onClick={() => toggleActive(r)} className="text-[12px] text-text-muted hover:text-negative disabled:opacity-50">
                    {r.isActive ? "Deactivate" : "Reactivate"}
                  </button>
                </span>
              );
            },
          },
        ]}
      />
      <CardBody className="pt-3">
        {err && <div className="mb-2 text-[12px] text-negative">{err}</div>}
        <p className="text-[11px] text-text-muted">
          Deactivating a brand hides it from new assignments. Existing clients, prospects and team memberships keep it,
          and their proposals and contracts keep its branding.
        </p>
        {form && (
          <DivisionForm
            key={form.mode === "edit" ? `edit-${form.division.id}` : "add"}
            form={form}
            onCancel={() => setForm(null)}
            onSaved={(m) => { setForm(null); onSaved(m); }}
          />
        )}
      </CardBody>
    </Card>
  );
}

function DivisionForm({ form, onCancel, onSaved }) {
  const api = useApiAction();
  const d = form.division ?? {};
  const [values, setValues] = useState({
    name: d.name || "",
    description: d.description || "",
    accentColor: d.accentColor || "",
    logoUrl: d.logoUrl || "",
  });
  const set = (k) => (v) => setValues((f) => ({ ...f, [k]: v }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const body = {
        name: values.name.trim(),
        description: values.description.trim() || null,
        accentColor: values.accentColor.trim() || null,
        logoUrl: values.logoUrl.trim() || null,
      };
      if (form.mode === "add") {
        await api("/api/divisions", { method: "POST", body: JSON.stringify(body) });
        onSaved("Division added.");
      } else {
        await api(`/api/divisions/${d.id}`, { method: "PUT", body: JSON.stringify(body) });
        onSaved("Division updated.");
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 pt-3 border-t border-border-light">
      <SectionLabel>{form.mode === "add" ? "Add division" : `Edit ${d.name}`}</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 max-w-[600px]">
        <Field label="Name" required value={values.name} onChange={set("name")} autoFocus />
        <Field label="Accent color" placeholder="#ff6804" value={values.accentColor} onChange={set("accentColor")} />
        <Field label="Logo URL" placeholder="https://…" value={values.logoUrl} onChange={set("logoUrl")} />
        <Field label="Description" value={values.description} onChange={set("description")} />
      </div>
      {err && <div className="mt-2 text-[12px] text-negative">{err}</div>}
      <div className="mt-3 flex items-center gap-2">
        <button type="button" disabled={busy || !values.name.trim()} onClick={save} className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50">
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className="h-8 px-3 rounded border border-border-default text-[12px] text-text-secondary hover:text-text-primary disabled:opacity-50">
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Capabilities — skills team members are assigned. Same 409-without-an-
 * organization gate on POST as divisions; slug is immutable everywhere (never
 * sent on PUT), and reordering uses the bulk /reorder endpoint since it
 * exists here (divisions don't have one, hence the swap-two-rows approach
 * above).
 */
function CapabilitiesCard({ organization, capabilities, onSaved }) {
  const api = useApiAction();
  const [form, setForm] = useState(null); // { mode: "add" } | { mode: "edit", capability }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const sorted = [...capabilities].sort((a, b) => a.sortOrder - b.sortOrder);
  const canAdd = !!organization;

  async function toggleActive(c) {
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/capabilities/${c.id}`, { method: "PUT", body: JSON.stringify({ isActive: !c.isActive }) });
      onSaved(c.isActive ? `${c.name} deactivated.` : `${c.name} reactivated.`);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function move(i, dir) {
    const ids = sorted.map((c) => c.id);
    [ids[i], ids[i + dir]] = [ids[i + dir], ids[i]];
    setBusy(true);
    setErr(null);
    try {
      await api("/api/capabilities/reorder", { method: "PUT", body: JSON.stringify({ ids }) });
      onSaved("Reordered.");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title="Capabilities"
        subtitle="Skills team members can be assigned"
        right={
          <button
            type="button"
            disabled={!canAdd}
            title={canAdd ? undefined : "Create the organization first — run seed-organization.js"}
            onClick={() => setForm({ mode: "add" })}
            className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50"
          >
            + Add Capability
          </button>
        }
      />
      <DataTable
        emptyLabel="No capabilities yet."
        rows={sorted}
        columns={[
          { key: "name", label: "Capability", render: (r) => <span className="font-medium text-text-primary">{r.name}</span> },
          { key: "slug", label: "Slug", render: (r) => <span className="font-mono text-[12px] text-text-muted">{r.slug}</span> },
          { key: "memberCount", label: "Members", align: "right", render: (r) => r.memberCount ?? 0 },
          { key: "isActive", label: "Status", render: (r) => (r.isActive ? "Active" : "Inactive") },
          {
            key: "actions", label: "", align: "right",
            render: (r) => {
              const i = sorted.findIndex((x) => x.id === r.id);
              return (
                <span className="inline-flex items-center gap-2 justify-end whitespace-nowrap">
                  <button type="button" disabled={busy || i === 0} onClick={() => move(i, -1)} className="text-text-muted disabled:opacity-30" aria-label="Move up">↑</button>
                  <button type="button" disabled={busy || i === sorted.length - 1} onClick={() => move(i, 1)} className="text-text-muted disabled:opacity-30" aria-label="Move down">↓</button>
                  <button type="button" onClick={() => setForm({ mode: "edit", capability: r })} className="text-[12px] text-accent hover:underline">Edit</button>
                  <button type="button" disabled={busy} onClick={() => toggleActive(r)} className="text-[12px] text-text-muted hover:text-negative disabled:opacity-50">
                    {r.isActive ? "Deactivate" : "Reactivate"}
                  </button>
                </span>
              );
            },
          },
        ]}
      />
      <CardBody className="pt-3">
        {err && <div className="mb-2 text-[12px] text-negative">{err}</div>}
        {form && (
          <CapabilityForm
            key={form.mode === "edit" ? `edit-${form.capability.id}` : "add"}
            form={form}
            onCancel={() => setForm(null)}
            onSaved={(m) => { setForm(null); onSaved(m); }}
          />
        )}
      </CardBody>
    </Card>
  );
}

function CapabilityForm({ form, onCancel, onSaved }) {
  const api = useApiAction();
  const c = form.capability ?? {};
  const [values, setValues] = useState({ name: c.name || "", description: c.description || "" });
  const set = (k) => (v) => setValues((f) => ({ ...f, [k]: v }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const body = { name: values.name.trim(), description: values.description.trim() || null };
      if (form.mode === "add") {
        await api("/api/capabilities", { method: "POST", body: JSON.stringify(body) });
        onSaved("Capability added.");
      } else {
        await api(`/api/capabilities/${c.id}`, { method: "PUT", body: JSON.stringify(body) });
        onSaved("Capability updated.");
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 pt-3 border-t border-border-light">
      <SectionLabel>{form.mode === "add" ? "Add capability" : `Edit ${c.name}`}</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 max-w-[600px]">
        <Field label="Name" required value={values.name} onChange={set("name")} autoFocus />
        {form.mode === "edit" && (
          <div>
            <FieldLabel>Slug</FieldLabel>
            <input
              value={c.slug || ""}
              disabled
              className="w-full h-9 px-3 rounded border border-border-light bg-app-bg text-[13px] text-text-muted font-mono"
            />
            <div className="text-[10px] text-text-muted mt-1">Key is fixed — renaming is safe and never breaks assignments.</div>
          </div>
        )}
        <Field label="Description" value={values.description} onChange={set("description")} />
      </div>
      {err && <div className="mt-2 text-[12px] text-negative">{err}</div>}
      <div className="mt-3 flex items-center gap-2">
        <button type="button" disabled={busy || !values.name.trim()} onClick={save} className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 disabled:opacity-50">
          {busy ? "Saving…" : "Save"}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className="h-8 px-3 rounded border border-border-default text-[12px] text-text-secondary hover:text-text-primary disabled:opacity-50">
          Cancel
        </button>
      </div>
    </div>
  );
}
